import { Page } from './../../shared/models/page';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { Reservation } from '../models/reservation';
import { ReservationService } from './reservation-http-service';
import { ReservationStatusCode } from 'app/event/models/reservation-status-code.enum';
import { RoomStaySummary } from '../models/room-stay-summary';
import { ReservationUtils } from '../utils/reservation-utils';
import { EventStore } from 'app/event/service/event.store';
import { OnyxEvent } from '../models/event';
import { EventReservationSource } from '../models/event-reservation-source';
import { TransientReservationService } from './transient-reservation-http-service';
import { TransactionUtils } from '../utils/transaction-utils';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ReservationStore {

  private currentReservations: BehaviorSubject<List<Reservation>> = new BehaviorSubject(List([]));

  constructor(
    private flashMessagesService: FlashMessagesService,
    private reservationHttpService: ReservationService,
    private eventStore: EventStore,
    private transientReservationService: TransientReservationService,
    private translateService: TranslateService
  ) {}

  get reservations(): Observable<List<Reservation>> {
    return this.currentReservations.asObservable();
  }

  /**
   * Has to be called to retrieve reservations.
   * Is called by resolver.
   *
   * @param eventId
   * @returns RESERVATION_LIST
   */
  loadReservations(eventId: number): Observable<List<Reservation>> {

    return this.reservationHttpService.get(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RESERVATIONS',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<Reservation>) => {
        this.currentReservations.next(reservationList);
        return reservationList;
      });
  }

  /**
   * Gets the filtered reservation list by an array of status codes and transforms the list into a room stay summary
   *
   * @param statusCode{ReservationStatusCode[]}
   * @returns {Observable<StaySummary>}
   */
  getRoomStaySummaryFilteredByStatus(statusCodes: ReservationStatusCode[]): Observable<RoomStaySummary> {
    return this.getReservationListFilteredByStatus(statusCodes).map(reservationList => {
      return TransactionUtils.transformTransactionsToSummary(reservationList, this.translateService);
    });
  }

  /**
   * Filters reservation list by an array of status codes
   * e.g. checked out
   *
   * @param statusCode{ReservationStatusCode[]}
   * @returns {Observable<List<Reservation>>}
   */
  getReservationListFilteredByStatus(statusCodes: ReservationStatusCode[]): Observable<List<Reservation>> {
    return this.currentReservations.asObservable().map((reservationList: List<Reservation>) => {
      return reservationList.filter((reservation: Reservation) => {
        return statusCodes.find(statusCode => statusCode.toString() === reservation.status) != null;
      }).toList();
    });
  }

  getTotalCommissions(reservationStatusCode: ReservationStatusCode): Observable<number> {
    let commissionPercentage = null;
    return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
      commissionPercentage = event.commissionRoomPercentage;
      return this.getRoomStaySummaryFilteredByStatus([reservationStatusCode]);
    }).map((summary: RoomStaySummary) => {
      return summary.staySummary.totalCommission;
    });
  }

  getTotalRevenue(reservationStatusCode: ReservationStatusCode): Observable<number> {
    let commissionPercentage = null;
    return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
      commissionPercentage = event.commissionRoomPercentage;
      return this.getRoomStaySummaryFilteredByStatus([reservationStatusCode]);
    }).map((summary: RoomStaySummary) => {
      return summary.staySummary.totalRevenue;
    });
  }

  addReservation(reservation: Reservation, source: EventReservationSource): Observable<any> {
    return this.reservationHttpService.add(reservation, source)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_RESERVATION', `Error adding reservation: ${error}`);
        return Observable.throw(error);
      })
      .map((response) => {
        this.flashMessagesService.success(
          'EVENT.RESERVATION.RESERVATION_ADDED',
          null, { lastName: String (reservation.lastName), firstName: String (reservation.firstName) } );
        this.loadReservations(reservation.eventId).subscribe();
        return response;
      });
  }

  /**
   * Delete a reservation
   *
   * @param reservation
   * @returns {Observable<R>}
   */
  deleteReservation(reservation: Reservation): Observable<any> {
    return this.reservationHttpService.delete(reservation)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.DELETE_RESERVATION', `Error deleting reservation: ${error}`);
        return Observable.throw(error);
      })
      .map(response => {
        this.flashMessagesService.success('EVENT.RESERVATION.RESERVATION_DELETED');
        this.loadReservations(reservation.eventId).subscribe();
        return response;
      });
  }

  /**
   * Update a reservation
   *
   * @param reservation
   * @param source
   * @returns {OperatorFunction<T, R>}
   */
  updateReservation(reservation: Reservation): Observable<any> {
    return this.reservationHttpService.update(reservation)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_RESERVATION', `Error updating reservation: ${error}`);
        return Observable.throw(error);
      })
      .map((response) => {
        this.flashMessagesService.success(
          'EVENT.RESERVATION.RESERVATION_UPDATED',
          null, { lastName: String (reservation.lastName), firstName: String (reservation.firstName) } );
        this.loadReservations(reservation.eventId).subscribe();
        return response;
      });
  }

  /**
   * Import a file of reservations
   *
   * @param eventId
   * @param file
   * @returns {OperatorFunction<T, R>}
   */
  importReservationFile(eventId: number, file: File): Observable<any> {
    return this.reservationHttpService
      .importReservationFile(eventId, file)
      .map((importResponse) => {
        this.loadReservations(eventId).subscribe();
        return importResponse;
      });
  }

  /**
   * Unlink an out-of-block reservation
   *
   * @param reservation
   * @returns {Observable<R>}
   */
  unlinkReservation(eventReservationId: number, eventId: number): Observable<any> {
    return this.transientReservationService.unlinkTransientReservation(eventReservationId, eventId)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UNLINK_RESERVATION', `Error unlinking reservation: ${error}`);
        return Observable.throw(error);
      })
      .map(response => {
        this.flashMessagesService.success('EVENT.TRANSIENT_RESERVATION.RESERVATION_UNLINKED');
        this.loadReservations(eventId).subscribe();
        return response;
      });
  }

  getReservationsByStatus(eventId: number, statusCodes: ReservationStatusCode[], pageNumber: number, size: number)
  : Observable<Page<Reservation>> {

    return this.reservationHttpService.getReservationsByStatus(eventId, statusCodes, pageNumber, size)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RESERVATIONS',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      });
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/service/reservation-store.ts